import React, { useMemo, useState } from 'react';

import SelectField from '../../components/form/SelectField';
import usStates from '../../lib/usStates';

export interface StateSelectFieldProps {
  name: string;
}

export interface StateOption {
  label: string;
  value: string;
}

// These forms have always offered PR but not VI. VI lives in the canonical
// list for the rescue portal's hardware order forms, so filter it back out
// here rather than changing where consumer orders can ship.
const stateOptions: StateOption[] = usStates
  .filter(({ abbreviation }) => abbreviation !== 'VI')
  .map(({ name, abbreviation }) => ({
    label: name,
    value: abbreviation,
  }));

/**
 * Whether a state matches what has been typed: the start of its postal code, or the start of any
 * word in its name. react-select's default matches anywhere, so "wa" offered Delaware, Hawaii and
 * Iowa ahead of Washington.
 */
export function stateMatches(option: StateOption, input: string) {
  const typed = input.trim().toLowerCase();
  if (!typed) {
    return true;
  }
  if (option.value.toLowerCase().startsWith(typed)) {
    return true;
  }
  const name = option.label.toLowerCase();
  return name.startsWith(typed) || name.split(/\s+/).some((word) => word.startsWith(typed));
}

/**
 * The options with an exact postal code match first, so typing "ar" or "ma" lands on Arkansas or
 * Massachusetts rather than the first state alphabetically. Every option stays in the list, since the
 * field looks its current value up in it.
 *
 * Moving it to the top isn't enough to highlight it: react-select v3 keeps the highlighted option
 * while that object is still in the filtered list, so after "m" highlights Maine, "ma" would leave
 * Maine highlighted and Enter would pick it. Fresh copies match no earlier highlight, so react-select
 * falls back to the first option.
 */
export function orderStateOptions(options: StateOption[], input: string) {
  const typed = input.trim().toUpperCase();
  const exact = options.find((option) => option.value === typed);
  if (!exact) {
    return options;
  }
  return [exact, ...options.filter((option) => option !== exact)].map((option) => ({ ...option }));
}

export default function StateSelectField({ name }: StateSelectFieldProps) {
  const [input, setInput] = useState('');
  const options = useMemo(() => orderStateOptions(stateOptions, input), [input]);

  return (
    <SelectField
      menuPlacement="top"
      autoComplete="address-level1"
      name={name}
      placeholder="State"
      options={options}
      filterOption={(option: { data: StateOption }, typed: string) => stateMatches(option.data, typed)}
      onInputChange={(value: string) => setInput(value)}
    />
  );
}
